"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";

/**
 * Sends one page-view beacon per navigation to /api/f (see src/lib/funnel.ts).
 *
 * Keyed on the pathname, not the full URL: a few pages rewrite their own query
 * string after mount (GoogleTag strips the one-time `?signup=1`), and those
 * rewrites are the same page view, not a new one.
 */
export function FunnelBeacon() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;

    // Read the live URL rather than rebuilding it: the campaign parameters
    // must arrive exactly as the ad click delivered them.
    const url = window.location.pathname + window.location.search;
    const payload = JSON.stringify({
      u: url,
      r: document.referrer || "",
      w: window.innerWidth,
    });

    try {
      const blob = new Blob([payload], { type: "text/plain;charset=UTF-8" });
      // sendBeacon survives the page being navigated away from; fetch with
      // keepalive is the fallback where it's unavailable.
      if (!navigator.sendBeacon?.("/api/f", blob)) {
        void fetch("/api/f", { method: "POST", body: payload, keepalive: true }).catch(() => {});
      }
    } catch {
      /* measurement must never break the app */
    }
  }, [pathname, searchParams]);

  return null;
}
